import { useState, useEffect, useRef } from 'react';
import { X, Link2, Save, Sparkles, BookOpen, Globe } from 'lucide-react';
import { IconPicker } from './IconPicker';
import { supabase } from '../../lib/supabase';
import * as LucideIcons from 'lucide-react';
import * as SimpleIcons from 'react-icons/si';
import * as FontAwesome from 'react-icons/fa';
import * as MaterialDesign from 'react-icons/md';
import { useDialogA11y } from '../ui/useDialogA11y';

interface LinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (link: LinkFormData) => Promise<void>;
  link?: SharedLink | null;
}

export interface SharedLink {
  id: string;
  titulo: string;
  descripcion: string | null;
  url: string;
  icono: string;
  orden: number;
  activo: boolean;
  apply_to_all_courses?: boolean;
}

export interface LinkFormData {
  titulo: string;
  descripcion: string;
  url: string;
  icono: string;
  activo: boolean;
  apply_to_all_courses: boolean;
  selectedCourses: string[];
}

interface Course {
  id: string;
  name: string;
  description: string | null;
}

export function LinkModal({ isOpen, onClose, onSave, link }: LinkModalProps) {
  const [formData, setFormData] = useState<LinkFormData>({
    titulo: '',
    descripcion: '',
    url: '',
    icono: 'lucide:Link',
    activo: true,
    apply_to_all_courses: true,
    selectedCourses: [],
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);

  useEffect(() => {
    if (isOpen) {
      loadCourses();
      if (link) {
        loadLinkAssignments(link.id);
      }
    }
  }, [isOpen, link]);

  useEffect(() => {
    if (link) {
      const normalizedIcon = link.icono.includes(':') ? link.icono : `lucide:${link.icono}`;
      setFormData({
        titulo: link.titulo,
        descripcion: link.descripcion || '',
        url: link.url,
        icono: normalizedIcon,
        activo: link.activo,
        apply_to_all_courses: link.apply_to_all_courses ?? true,
        selectedCourses: [],
      });
    } else {
      setFormData({
        titulo: '',
        descripcion: '',
        url: '',
        icono: 'lucide:Link',
        activo: true,
        apply_to_all_courses: true,
        selectedCourses: [],
      });
    }
    setErrors({});
  }, [link, isOpen]);

  const loadCourses = async () => {
    setLoadingCourses(true);
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('id, name, description')
        .order('name', { ascending: true });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoadingCourses(false);
    }
  };

  const loadLinkAssignments = async (linkId: string) => {
    try {
      const { data, error } = await supabase
        .from('link_course_assignments')
        .select('course_id')
        .eq('link_id', linkId);

      if (error) throw error;

      const assignedCourseIds = (data || []).map(a => a.course_id);
      setFormData(prev => ({
        ...prev,
        selectedCourses: assignedCourseIds,
      }));
    } catch (error) {
      console.error('Error loading link assignments:', error);
    }
  };

  const getIconComponent = (iconString: string) => {
    const [library, iconName] = iconString.includes(':')
      ? iconString.split(':')
      : ['lucide', iconString];

    try {
      if (library === 'si') {
        return (SimpleIcons as any)[iconName];
      } else if (library === 'fa') {
        return (FontAwesome as any)[iconName];
      } else if (library === 'md') {
        return (MaterialDesign as any)[iconName];
      } else {
        return (LucideIcons as any)[iconName];
      }
    } catch {
      return LucideIcons.Link;
    }
  };

  const getIconLabel = (iconString: string) => {
    const parts = iconString.split(':');
    if (parts.length === 2) {
      return parts[1].replace(/^Si/, '').replace(/([A-Z])/g, ' $1').trim();
    }
    return iconString;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.titulo.trim()) {
      newErrors.titulo = 'El título es requerido';
    }

    if (!formData.url.trim()) {
      newErrors.url = 'La URL es requerida';
    } else {
      try {
        new URL(formData.url);
      } catch {
        newErrors.url = 'La URL no es válida';
      }
    }

    if (!formData.apply_to_all_courses && formData.selectedCourses.length === 0) {
      newErrors.courses = 'Debes seleccionar al menos un curso o marcar "Aplicar a todos los cursos"';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCourseToggle = (courseId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedCourses: prev.selectedCourses.includes(courseId)
        ? prev.selectedCourses.filter(id => id !== courseId)
        : [...prev.selectedCourses, courseId],
    }));
  };

  const handleSelectAllCourses = () => {
    setFormData(prev => ({
      ...prev,
      selectedCourses: prev.selectedCourses.length === courses.length
        ? []
        : courses.map(c => c.id),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving link:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-link"
        tabIndex={-1}
        className="glass-surface rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 glass-surface border-b border-white/[0.08] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cubus-lime/10 rounded-lg">
              <Link2 className="w-5 h-5 text-cubus-lime" />
            </div>
            <h2 id="modal-title-link" className="text-xl font-bold text-white">
              {link ? 'Editar Link' : 'Nuevo Link'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/[0.04] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Título *
            </label>
            <input
              type="text"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:border-cubus-lime"
              placeholder="Ej: Grabaciones de Clases"
            />
            {errors.titulo && (
              <p className="mt-1 text-sm text-red-500">{errors.titulo}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Descripción
            </label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:border-cubus-lime resize-none"
              rows={3}
              placeholder="Descripción opcional del link"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              URL *
            </label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:border-cubus-lime"
              placeholder="https://ejemplo.com"
            />
            {errors.url && (
              <p className="mt-1 text-sm text-red-500">{errors.url}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Ícono
            </label>
            <button
              type="button"
              onClick={() => setShowIconPicker(true)}
              className="w-full px-4 py-3 glass-deep border border-white/[0.08] rounded-lg text-white hover:border-cubus-lime/40 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                {(() => {
                  const IconComponent = getIconComponent(formData.icono);
                  return <IconComponent className="w-5 h-5 text-cubus-lime" />;
                })()}
                <span className="text-white/70">{getIconLabel(formData.icono)}</span>
              </div>
              <Sparkles className="w-5 h-5 text-cubus-lime" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="activo"
              checked={formData.activo}
              onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              className="w-4 h-4 glass-deep border-white/[0.08] rounded focus:ring-cubus-lime"
            />
            <label htmlFor="activo" className="text-sm text-white/70">
              Link activo (visible para los estudiantes)
            </label>
          </div>

          <div className="border-t border-white/[0.08] pt-6">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="apply_to_all_courses"
                checked={formData.apply_to_all_courses}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    apply_to_all_courses: e.target.checked,
                    selectedCourses: e.target.checked ? [] : formData.selectedCourses
                  });
                }}
                className="w-4 h-4 glass-deep border-white/[0.08] rounded focus:ring-cubus-lime"
              />
              <label htmlFor="apply_to_all_courses" className="text-sm font-medium text-white/70 flex items-center gap-2">
                <Globe className="w-4 h-4 text-cubus-lime" />
                Aplicar a todos los cursos (link global)
              </label>
            </div>

            {!formData.apply_to_all_courses && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-white/70 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-cubus-lime" />
                    Seleccionar cursos específicos *
                  </label>
                  <button
                    type="button"
                    onClick={handleSelectAllCourses}
                    className="text-xs text-cubus-lime hover:underline"
                  >
                    {formData.selectedCourses.length === courses.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                  </button>
                </div>

                {loadingCourses ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cubus-lime"></div>
                  </div>
                ) : courses.length === 0 ? (
                  <div className="text-center py-8 text-muted text-sm">
                    No hay cursos disponibles
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto glass-deep border border-white/[0.08] rounded-lg p-3 space-y-2">
                    {courses.map(course => (
                      <label
                        key={course.id}
                        className="flex items-center gap-3 p-3 hover:bg-white/[0.06] rounded-lg cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.selectedCourses.includes(course.id)}
                          onChange={() => handleCourseToggle(course.id)}
                          className="w-4 h-4 glass-deep border-white/[0.08] rounded focus:ring-cubus-lime"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white">{course.name}</div>
                          {course.description && (
                            <div className="text-xs text-muted">{course.description}</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {errors.courses && (
                  <p className="text-sm text-red-500">{errors.courses}</p>
                )}

                {formData.selectedCourses.length > 0 && (
                  <div className="text-xs text-muted">
                    {formData.selectedCourses.length} curso{formData.selectedCourses.length !== 1 ? 's' : ''} seleccionado{formData.selectedCourses.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-white/[0.08]">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 glass-deep hover:bg-white/[0.06] text-white rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white/[0.08]"></div>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Guardar
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <IconPicker
        isOpen={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        onSelect={(iconName) => setFormData({ ...formData, icono: iconName })}
        currentIcon={formData.icono}
      />
    </div>
  );
}
